'use client';

import { useState } from 'react';
import { useParams } from 'next/navigation';
import PageLayout from '../PageLayout';
import { defaultLang, SUPPORTED } from '../../config/languages';
import styles from './GDPRRequestForm.module.css';

const API_BASE_URL = (process.env.NEXT_PUBLIC_API_BASE_URL || 'https://api.dev.adcast.app').replace(/\/$/, '');

const requestTypes = [
  ['access', 'Access my personal data'],
  ['rectification', 'Correct my personal data'],
  ['erasure', 'Delete my personal data'],
  ['restriction', 'Restrict processing of my personal data'],
  ['portability', 'Receive a portable copy of my data'],
  ['objection', 'Object to processing of my personal data'],
  ['other', 'Other GDPR request'],
];

export default function GDPRRequestForm() {
  const { lang = defaultLang } = useParams() || {};
  const activeLang = SUPPORTED.includes(lang) ? lang : defaultLang;
  const [status, setStatus] = useState({ type: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    const form = event.currentTarget;
    const formData = new FormData(form);
    const payload = {
      name: formData.get('name'),
      email: formData.get('email'),
      accountEmail: formData.get('accountEmail'),
      requestType: formData.get('requestType'),
      country: formData.get('country'),
      details: formData.get('details'),
      confirmation: formData.get('confirmation') === 'on',
      website: formData.get('website'),
    };

    setSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      const response = await fetch(`${API_BASE_URL}/gdpr-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) throw new Error(data.message || 'We could not submit your request. Please try again later.');

      form.reset();
      setStatus({ type: 'success', message: data.message || 'Your GDPR request has been received.' });
    } catch (error) {
      setStatus({ type: 'error', message: error.message || 'We could not submit your request. Please try again later.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageLayout
      showWorkspaceCta
      title="Submit a GDPR Request"
      metaDescription="Submit a request to access, correct, delete, restrict or export your personal data held by AdCast."
      canonical={`https://www.adcast.app${activeLang === 'en' ? '' : `/${activeLang}`}/gdpr-requests`}
      noindex
      showGetInTouch={false}
    >
      <article className={`single_blog_content ${styles.page}`}>
        <h1>Submit a GDPR Request</h1>
        <p>Use this form to exercise your data protection rights. We may contact you to verify your identity before processing the request.</p>

        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.grid}>
            <label>
              Full name <span aria-hidden="true">*</span>
              <input name="name" type="text" autoComplete="name" maxLength="120" required />
            </label>
            <label>
              Contact email <span aria-hidden="true">*</span>
              <input name="email" type="email" autoComplete="email" maxLength="254" required />
            </label>
            <label>
              Account email, if different
              <input name="accountEmail" type="email" autoComplete="email" maxLength="254" />
            </label>
            <label>
              Country or region
              <input name="country" type="text" autoComplete="country-name" maxLength="100" />
            </label>
          </div>

          <label>
            Request type <span aria-hidden="true">*</span>
            <select name="requestType" defaultValue="" required>
              <option value="" disabled>Select a request type</option>
              {requestTypes.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>

          <label>
            Tell us how we can help <span aria-hidden="true">*</span>
            <textarea name="details" rows="7" maxLength="5000" required />
          </label>

          <label className={styles.honeypot} aria-hidden="true">
            Website
            <input name="website" type="text" tabIndex="-1" autoComplete="off" />
          </label>

          <label className={styles.confirmation}>
            <input name="confirmation" type="checkbox" required />
            <span>I confirm that the information in this request is accurate and that I am authorized to make this request.</span>
          </label>

          {status.message && (
            <p className={`${styles.status} ${status.type === 'success' ? styles.statusSuccess : styles.statusError}`} role="status">
              {status.message}
            </p>
          )}

          <button className={`signup_btn3 ${styles.submit}`} type="submit" disabled={submitting}>
            {submitting ? 'Submitting request…' : 'Submit GDPR request'}
          </button>
        </form>
      </article>
    </PageLayout>
  );
}
